import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  Modal,
  Select,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { GridColDef } from "@mui/x-data-grid";
import SearchBar from "components/SearchBar";
import TableComponent from "components/TableComponent";
import MainContainer from "layout/MainContainer";
import DatePicker from "Modals/datePicker";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApplicationType, CommonBody, Listing } from "types/General";
import { isValidInput } from "utils/validations";
import dayjs, { Dayjs } from "dayjs";
import {
  useChangeInvestmentListStatusMutation,
  useLazyGetInvestmentUserListQuery,
} from "service/investment";
import { showError, showToast } from "helpers";
import { INVESTMENT_STATUS, INVESTMENT_TYPE } from "utils/enums";
import { generateEncryptedKeyBody } from "utils/crypto";
import CustomPagination from "components/Panigation";

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const InvestorApplications = () => {
  const navigate = useNavigate();
  const [getInvestmentUserList] = useLazyGetInvestmentUserListQuery();
  const [changeStatus] = useChangeInvestmentListStatusMutation();

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [open, setOpen] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [valueTabs, setValueTabs] = React.useState(0);
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs("2022-04-17"));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs("2022-04-21"));
  const [investmentData, setInvestmentData] = useState([]);
  const [selectedApplication, setSelectedApplication] =
    useState<ApplicationType | null>(null);

  const [page, setPage] = useState<number>(1);

  const handlePageChange = (event: any, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
  };

  const columns: GridColDef[] = [
    {
      flex: 0.6,
      minWidth: 200,
      field: "name",
      headerName: "Name",
      editable: false,
      sortable: false,

      renderCell: ({ row }) => {
        return (
          <div className="user_block">
            <figure
              onClick={() => navigate(`/profile-detail/individual/${row?._id}`)}
            >
              <img src={"/static/images/user_placeholder.png"} alt="" />
            </figure>
            <p>
              <b>{row?.individualDetails?.name || "-"}</b>
              {row?.individualDetails?.email || "-"}
            </p>
          </div>
        );
      },
    },

    {
      flex: 0.4,
      minWidth: 100,
      field: "country",
      headerName: "Country",
      editable: false,
      sortable: false,

      renderCell: ({ row }) => {
        return (
          <div className="user_block">
            <p>{row?.individualDetails?.country || "-"}</p>
          </div>
        );
      },
    },
    {
      flex: 0.4,
      minWidth: 100,
      field: "type",
      headerName: "Investment Type",
      editable: false,
      sortable: false,

      renderCell: ({ row }) => {
        return (
          <div className="user_block">
            <p>
              {row?.investmentType === INVESTMENT_TYPE.ONLINE
                ? "Online"
                : "Offline"}
            </p>
          </div>
        );
      },
    },
    {
      flex: 0.4,
      minWidth: 100,
      field: "plan",
      headerName: "Plan",
      editable: false,
      sortable: false,

      renderCell: ({ row }) => {
        return (
          <div className="user_block">
            <p>{row?.planData?.name || "-"}</p>
          </div>
        );
      },
    },
    {
      flex: 0.4,
      minWidth: 100,
      field: "date",
      headerName: "Date",
      editable: false,
      sortable: false,

      renderCell: ({ row }) => {
        return (
          <div className="user_block">
            <p>{dayjs(row?.createdAt).format("DD/MM/YYYY")}</p>
          </div>
        );
      },
    },

    {
      minWidth: 200,
      field: "request",
      headerName: "Request",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => (
        <div style={{ display: "flex", gap: 20 }}>
          <Button
            onClick={() => {
              changeApplicationStatus(3, row?._id);
            }}
            variant="outlined"
            sx={{
              color: "green",
              borderColor: "green",
              "&:hover": {
                backgroundColor: "rgba(0, 128, 0, 0.1)", // light green background on hover
                borderColor: "darkgreen",
                color: "darkgreen",
              },
            }}
            size="small"
          >
            Approve
          </Button>

          <Button
            onClick={() => {
              changeApplicationStatus(4, row?._id);
            }}
            variant="outlined"
            sx={{
              color: "red",
              borderColor: "red",
              "&:hover": {
                backgroundColor: "rgba(255, 0, 0, 0.1)",
                borderColor: "darkred",
                color: "darkred",
              },
            }}
            size="small"
          >
            Reject
          </Button>
        </div>
      ),
    },

    {
      minWidth: 130,
      field: "action",
      headerName: "Action",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => (
        <div className="table_actions v2">
          <Box>
            <IconButton
              onClick={() => {
                setSelectedApplication(row);
                setOpenModal(true);
              }}
            >
              <img src="/static/images/eye_icon.svg" alt="" />
            </IconButton>
          </Box>
        </div>
      ),
    },
  ];

  const changeApplicationStatus = async (status: number, id?: string) => {
    setLoading(true);
    try {
      const body = {
        status,
      };

      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;

      const response = await changeStatus({
        id: selectedApplication?._id || id,
        body: encryptedBody,
      }).unwrap();
      setLoading(false);
      if (response.statusCode === 200) {
        showToast(response?.message || "");
        setOpenModal(false);
        getInvestmentUserListApi();
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const getInvestmentUserListApi = async () => {
    setLoading(true);
    try {
      const response = await getInvestmentUserList({
        page: page,
        search: debouncedSearchTerm,
        status:
          valueTabs === 0
            ? INVESTMENT_STATUS.APPLIED
            : valueTabs === 1
              ? INVESTMENT_STATUS.APPROVED
              : INVESTMENT_STATUS.APPLIED,
      }).unwrap();
      setLoading(false);
      if (response.statusCode === 200) {
        setInvestmentData(response?.data?.list || []);
        setCount(response?.data?.pagination?.total);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  useEffect(() => {
    getInvestmentUserListApi();
  }, [page, valueTabs]);

  return (
    <MainContainer>
      <div className="main_title">
        <h1>Investor Applications</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span> -
          Investor Applications
        </p>
      </div>
      <div className="cards">
        <Box className="custom_tabs" sx={{ width: "100%" }}>
          <div className="table_header">
            <div className="left_s">
              <Box className="search_box">
                <SearchBar
                  searchTerm={searchTerm}
                  placeholder="Search"
                  setDebouncedSearchTerm={setDebouncedSearchTerm}
                  value={searchTerm}
                  onCross={() => setSearchTerm("")}
                  onChange={(val: any) => {
                    if (isValidInput(val.target.value)) {
                      setSearchTerm(val.target.value);
                    }
                  }}
                />
              </Box>
              {/* <Box className="search_box">
                <Box className="search_bar">
                  <TextField
                    // value={value}
                    // onChange={onChange}
                    onClick={() => setOpen(true)}
                    hiddenLabel
                    placeholder={"Select date"}
                    inputProps={{ readOnly: true }}
                    className="txt_inpt"
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="end" className="search_icon">
                          <CalendarTodayIcon />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment
                          position="end"
                          className="cross_btn"
                        ></InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Box> */}

              {/* <Box className="search_box">
                <Box className="search_bar">
                  <Select
                    fullWidth
                    labelId="condition"
                    name="condition"
                    value={"default"}

                    // onChange={formik.handleChange}
                    // onBlur={formik.handleBlur}
                    // error={formik.touched.condition && Boolean(formik.errors.condition)}
                  >
                    <MenuItem disabled value="default">
                      Country
                    </MenuItem>

                    <MenuItem value={1}>Uk</MenuItem>
                    <MenuItem value={2}>Canada</MenuItem>
                  </Select>
                </Box>
              </Box> */}
            </div>
            <div className="right_s"></div>
          </div>

          <Tabs
            value={valueTabs}
            onChange={handleChangeTabs}
            aria-label="basic tabs example"
            className="custom_tabs_links"
          >
            <Tab label="Pending" {...a11yProps(0)} />
            <Tab label="Approved" {...a11yProps(1)} />
          </Tabs>

          <div className="sorting_table">
            <TableComponent
              columns={columns}
              rows={investmentData}
              count={count}
              page={page}
              onPageChange={handlePageChange}
              loading={loading}
            />

            {investmentData?.length ? (
              <CustomPagination
                currentPage={page - 1}
                totalPages={Math.ceil(count / 10)}
                count={count}
                pageSize={10}
                onPageChange={(page) => setPage(page + 1)}
              />
            ) : null}
          </div>
        </Box>
      </div>
      <DatePicker
        open={open}
        setOpen={setOpen}
        onClose={() => setOpen(false)}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
      />

      <Modal
        className="modal addNotification_modal"
        id="addNotificationModal"
        aria-labelledby="addNotification-modal-title"
        aria-describedby="addNotification-modal-description"
        open={openModal}
        onClose={() => setOpenModal(false)}
      >
        <div className="modal-dialog">
          <div className="modal-body">
            <div className="btn-close" onClick={() => setOpenModal(false)}>
              <CloseIcon />
            </div>
            <div className="modal_title">
              <h2>Application Details</h2>
            </div>
            <div className="profileInfo_list">
              <ul className="text_info gap_p">
                <li className="w_50">
                  <strong>Name</strong>
                  <span>
                    {selectedApplication?.individualDetails?.name || "-"}
                  </span>
                </li>
                <li className="w_50">
                  <strong>E-mail</strong>
                  <span>
                    {selectedApplication?.individualDetails?.email || "-"}
                  </span>
                </li>
                <li className="w_50">
                  <strong>Phone</strong>
                  <span>
                    {selectedApplication?.individualDetails?.phone || "-"}
                  </span>
                </li>

                <li className="w_50">
                  <strong>Investment Type</strong>
                  <span>
                    {selectedApplication?.investmentType ===
                    INVESTMENT_TYPE.ONLINE
                      ? "Online"
                      : "Offline"}
                  </span>
                </li>

                <li className="w_50">
                  <strong>Plan</strong>
                  <span>{selectedApplication?.planData?.name || "-"}</span>
                </li>

                <li className="w_50">
                  <strong>Amount</strong>
                  <span>
                    AED{" "}
                    {selectedApplication?.planData?.minimumInvestment || "0"}
                  </span>
                </li>
              </ul>
            </div>

            <div style={{ marginTop: 30 }}>
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                onClick={() =>
                  window.open(selectedApplication?.signature, "_blank")
                }
              >
                Download Signed Agreement
              </Button>
            </div>

            {valueTabs === 0 ? (
              <div style={{ marginTop: 30, display: "flex", gap: 20 }}>
                <Button
                  onClick={() => changeApplicationStatus(3)}
                  variant="outlined"
                  sx={{
                    color: "green",
                    borderColor: "green",
                    "&:hover": {
                      backgroundColor: "rgba(0, 128, 0, 0.1)", // light green background on hover
                      borderColor: "darkgreen",
                      color: "darkgreen",
                    },
                  }}
                  size="small"
                >
                  Approve
                </Button>

                <Button
                  onClick={() => changeApplicationStatus(4)}
                  variant="outlined"
                  sx={{
                    color: "red",
                    borderColor: "red",
                    "&:hover": {
                      backgroundColor: "rgba(255, 0, 0, 0.1)",
                      borderColor: "darkred",
                      color: "darkred",
                    },
                  }}
                  size="small"
                >
                  Reject
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      </Modal>
    </MainContainer>
  );
};

export default InvestorApplications;
